import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle, Loader2, Search, X } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useTranslation } from "react-i18next";
import apiClient from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { getErrorMessage } from "@/utils/errorUtils";
import DonationPaymentDetailsModal, {
  type DonationCryptoDetails,
  type DonationPayerDetails,
} from "@/components/DonationPaymentDetailsModal";
import DonationCryptoPaymentModal, {
  type DonationCryptoPaymentData,
} from "@/components/DonationCryptoPaymentModal";
import { useSearchParams } from "react-router-dom";
import FeedbackModal from "@/components/FeedbackModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const quickAmounts = [10, 25, 50, 100, 150, 200, 250, 300];
const paymentMethods = ["Card", "Crypto"] as const;
const DEFAULT_CRYPTO_DETAILS: DonationCryptoDetails = {
  crypto: "USDT",
  chain: "SOLANA_DEVNET",
  baseFiat: "USD",
};
const DEFAULT_PAYER_DETAILS: DonationPayerDetails = {
  email: "",
  phone: "",
  fullName: "",
};

interface TopDonator {
  rank: number;
  userId: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  profileImage?: string | null;
  totalAmount: number;
  donationCount: number;
  currencies?: string[];
  lastDonationAt?: string | null;
}

export default function SupportDonate() {
  const { theme } = useApp();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const hasAppliedQueryRef = useRef(false);

  const fallbackNgoOptions = t("enduser.donate.ngoOptions", {
    returnObjects: true,
    defaultValue: ["NGO Name"],
  }) as string[];

  const [fetchedNgos, setFetchedNgos] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingNgos, setIsLoadingNgos] = useState(true);
  const [selectedNgo, setSelectedNgo] = useState(() => {
    const org = searchParams.get("organisation") || searchParams.get("organization") || "";
    return org.trim() || (fallbackNgoOptions.length > 0 ? fallbackNgoOptions[0] : "");
  });
  const [name, setName] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<(typeof paymentMethods)[number]>("Card");
  const [isInitializing, setIsInitializing] = useState(false);
  const [isVerifyingCryptoPayment, setIsVerifyingCryptoPayment] = useState(false);
  const [showFlutterwaveIframe, setShowFlutterwaveIframe] = useState(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showCryptoPaymentModal, setShowCryptoPaymentModal] = useState(false);
  const [flutterwavePaymentLink, setFlutterwavePaymentLink] = useState<string | null>(null);
  const [cryptoPaymentData, setCryptoPaymentData] = useState<DonationCryptoPaymentData | null>(null);
  const [cryptoDetails, setCryptoDetails] = useState<DonationCryptoDetails>(DEFAULT_CRYPTO_DETAILS);
  const [payerDetails, setPayerDetails] = useState<DonationPayerDetails>(DEFAULT_PAYER_DETAILS);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ type: 'success', title: '', message: '' });
  const [pendingTxRef, setPendingTxRef] = useState<string | null>(null);
  const [pendingTransactionId, setPendingTransactionId] = useState<string | null>(null);
  const [pendingReference, setPendingReference] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [topDonators, setTopDonators] = useState<TopDonator[]>([]);
  const [isLoadingTopDonators, setIsLoadingTopDonators] = useState(true);
  const [prefillNgo, setPrefillNgo] = useState<{ id: string; name: string } | null>(null);
  const limit = 10;
  const cryptoLiveMode = (import.meta.env.VITE_CRYPTO_LIVE as string | undefined) || "live";
  const isCryptoTestMode = cryptoLiveMode.trim().toLowerCase() === "test";

  useEffect(() => {
    const orgParam =
      searchParams.get("organisation") ||
      searchParams.get("organization") ||
      "";
    const orgId = orgParam.trim();
    if (!orgId) return;

    let cancelled = false;
    const fetchOrg = async () => {
      try {
        const response = await apiClient.get(ENDPOINTS.ADMIN.ORGANIZATION_DETAIL(orgId));
        const payload = response.data?.data ?? response.data;
        if (!payload || cancelled) return;

        const ngoName =
          payload.name || payload.organizationName || "Unknown NGO";
        if (!ngoName || ngoName === "Unknown NGO") return;

        const option = {
          id: String(payload._id || payload.id || orgId),
          name: String(ngoName),
        };

        if (cancelled) return;
        setPrefillNgo(option);
        setFetchedNgos((prev) => {
          const exists = prev.some((ngo) => String(ngo.id) === option.id);
          return exists ? prev : [option, ...prev];
        });
        setSelectedNgo(option.id);
        hasAppliedQueryRef.current = true;
      } catch (error: unknown) {
        if (!cancelled) {
          console.error("Failed to fetch organization details", error);
        }
      }
    };

    fetchOrg();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    const fetchNgos = async () => {
      try {
        if (page === 1) {
          setIsLoadingNgos(true);
          setHasMore(true);
        } else {
          setIsLoadingMore(true);
        }

        const response = await apiClient.get(
          `${ENDPOINTS.DONATE.NGOS}?q=${encodeURIComponent(searchTerm)}&page=${page}&limit=${limit}`
        );
        const responseData = response.data;
        const data =
          responseData?.data?.ngos ||
          responseData?.data?.data ||
          responseData?.data ||
          responseData?.ngos ||
          responseData ||
          [];

        const processArray = (arr: Record<string, unknown>[]) =>
          arr
            .map((ngo: Record<string, unknown>) => {
              const ngoName = ngo.name || ngo.organizationName || "Unknown NGO";
              return {
                id: String(ngo._id || ngo.id || ngoName),
                name: String(ngoName),
              };
            })
            .filter((ngo) => ngo.name !== "Unknown NGO");

        let mappedNgos: { id: string; name: string }[] = [];

        if (Array.isArray(data)) {
          mappedNgos = processArray(data);
        } else if (data && typeof data === "object") {
          const items = data.items || data.data || [];
          if (Array.isArray(items)) {
            mappedNgos = processArray(items);
          }
        }

        if (cancelled) return;

        if (page === 1) {
          setFetchedNgos((prev) => {
            if (prev.length === 0) return mappedNgos;
            const merged = [...prev];
            mappedNgos.forEach((item) => {
              if (!merged.some((ngo) => String(ngo.id) === String(item.id))) {
                merged.push(item);
              }
            });
            return merged;
          });

          if (mappedNgos.length > 0) {
            setSelectedNgo((prev) => prev || mappedNgos[0].id);
          }
        } else if (mappedNgos.length > 0) {
          setFetchedNgos((prev) => [...prev, ...mappedNgos]);
        }

        if (mappedNgos.length < limit) {
          setHasMore(false);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          console.error("Failed to fetch NGOs", error);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingNgos(false);
          setIsLoadingMore(false);
        }
      }
    };

    fetchNgos();
    return () => {
      cancelled = true;
    };
  }, [page, searchTerm]);

  useEffect(() => {
    let cancelled = false;

    const fetchTopDonators = async () => {
      try {
        setIsLoadingTopDonators(true);
        const response = await apiClient.get(`${ENDPOINTS.DONATE.TOP_DONATORS}?limit=10`);
        const donators = response.data?.data?.donators;

        if (!cancelled) {
          setTopDonators(Array.isArray(donators) ? donators : []);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          console.error("Failed to fetch top donators", error);
          setTopDonators([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingTopDonators(false);
        }
      }
    };

    fetchTopDonators();

    return () => {
      cancelled = true;
    };
  }, []);

  const ngoOptions = fetchedNgos.length > 0
    ? fetchedNgos
    : prefillNgo
      ? [prefillNgo]
      : searchTerm.trim()
        ? []
        : fallbackNgoOptions.map((ngoName) => ({ id: ngoName, name: ngoName }));

  useEffect(() => {
    if (hasAppliedQueryRef.current) return;
    if (ngoOptions.length === 0) return;

    const orgParam =
      searchParams.get("organisation") ||
      searchParams.get("organization") ||
      "";
    const amountParam = searchParams.get("amount") || "";

    if (!orgParam && !amountParam) return;

    const trimmedOrg = orgParam.trim();
    const trimmedAmount = amountParam.trim();
    const isFetched = fetchedNgos.length > 0;
    let applied = false;

    if (trimmedAmount) {
      const parsed = Number(trimmedAmount);
      if (Number.isFinite(parsed) && parsed > 0) {
        if (quickAmounts.includes(parsed)) {
          setSelectedAmount(parsed);
          setCustomAmount("");
        } else {
          setCustomAmount(String(parsed));
          setSelectedAmount(null);
        }
        applied = true;
      }
    }

    if (trimmedOrg) {
      const normalized = trimmedOrg.toLowerCase();
      const match = ngoOptions.find((ngo) => {
        const id = String(ngo.id).toLowerCase();
        const ngoName = String(ngo.name).toLowerCase();
        return id === normalized || ngoName === normalized;
      });

      if (match) {
        setSelectedNgo(match.id);
        applied = true;
      } else if (!isFetched && !prefillNgo) {
        return;
      }
    }

    if (applied) {
      hasAppliedQueryRef.current = true;
    }
  }, [ngoOptions, searchParams, fetchedNgos.length]);

  const donateAmount = useMemo(() => {
    const custom = Number(customAmount);
    if (!Number.isNaN(custom) && custom > 0) return custom;
    return selectedAmount ?? 0;
  }, [customAmount, selectedAmount]);
  const selectedNgoLabel = useMemo(
    () => ngoOptions.find((ngo) => String(ngo.id) === String(selectedNgo))?.name || selectedNgo,
    [ngoOptions, selectedNgo]
  );
  const donorRows = useMemo(
    () =>
      topDonators.map((donator) => {
        const displayName =
          donator.fullName?.trim() ||
          [donator.firstName, donator.lastName].filter(Boolean).join(" ").trim() ||
          donator.email?.split("@")[0] ||
          "Anonymous";
        const primaryCurrency = donator.currencies?.[0] || "USD";
        const formattedAmount = new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: primaryCurrency,
          maximumFractionDigits: 2,
        }).format(Number(donator.totalAmount || 0));

        return {
          key: donator.userId || `${displayName}-${donator.rank}`,
          name: displayName,
          amount: formattedAmount,
        };
      }),
    [topDonators]
  );

  useEffect(() => {
    if (!showFlutterwaveIframe) return;

    const interval = setInterval(() => {
      const donateSuccess = localStorage.getItem("donateSuccess") === "true";
      if (donateSuccess) {
        localStorage.removeItem("donateSuccess");
        setShowFlutterwaveIframe(false);
        setFlutterwavePaymentLink(null);
        verifyDonation();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showFlutterwaveIframe]);

  const updatePayerDetails = (field: keyof DonationPayerDetails, value: string) => {
    setPayerDetails((prev) => ({ ...prev, [field]: value }));
  };

  const updateCryptoDetails = (field: keyof DonationCryptoDetails, value: string) => {
    setCryptoDetails((prev) => ({
      ...prev,
      [field]: value as DonationCryptoDetails[typeof field],
    }));
  };

  const openCryptoPaymentDetails = () => {
    if (donateAmount <= 0) {
      setErrorMsg("Please enter a valid amount to donate.");
      setTimeout(() => setErrorMsg(""), 3000);
      return;
    }

    if (!selectedNgo) {
      setErrorMsg("Please select an NGO.");
      setTimeout(() => setErrorMsg(""), 3000);
      return;
    }

    setPayerDetails((prev) => ({
      ...prev,
      fullName: prev.fullName || name.trim(),
    }));
    setShowPaymentDetailsModal(true);
  };

  const initializeDonation = async () => {
    if (donateAmount <= 0) {
      setErrorMsg("Please enter a valid amount to donate.");
      setTimeout(() => setErrorMsg(""), 3000);
      return;
    }

    if (!selectedNgo) {
      setErrorMsg("Please select an NGO.");
      setTimeout(() => setErrorMsg(""), 3000);
      return;
    }

    setIsInitializing(true);
    setErrorMsg("");

    try {
      const payload = {
        ngoId: selectedNgo,
        customAmount: customAmount !== "" ? Number(customAmount) : null,
        selectedAmount,
        donorName: payerDetails.fullName.trim() || name.trim() || undefined,
        email: payerDetails.email.trim() || undefined,
        phone: payerDetails.phone.trim() || undefined,
        paymentMethod: paymentMethod.toLowerCase() === "card" ? "card" : "crypto",
        ...(paymentMethod === "Crypto"
          ? {
              crypto: cryptoDetails.crypto,
              chain: isCryptoTestMode ? "BSC_TESTNET" : cryptoDetails.chain,
              baseFiat: cryptoDetails.baseFiat,
            }
          : {}),
      };

      const response = await apiClient.post(ENDPOINTS.DONATE.NGO_DONATE, payload);
      const paymentData = response.data?.data as DonationCryptoPaymentData | undefined;

      if (!response.data?.success || !paymentData) {
        throw new Error(response.data?.message || "Failed to initialize donation");
      }

      if (paymentData.paymentLink) {
        const { paymentLink, txRef, donationId } = paymentData;
        setShowPaymentDetailsModal(false);

        setPendingTxRef(txRef || null);
        setPendingTransactionId(donationId || null);
        setPendingReference(null);

        localStorage.setItem("pendingCheckoutFlow", "donate");
        if (txRef) localStorage.setItem("pendingDonateTxRef", txRef);
        if (donationId) localStorage.setItem("pendingDonateId", donationId);
        localStorage.removeItem("pendingDonateReference");

        setFlutterwavePaymentLink(paymentLink);
        setShowFlutterwaveIframe(true);
        return;
      }

      const isCryptoInit =
        paymentMethod === "Crypto" &&
        !paymentData.paymentLink &&
        !!(
          paymentData.address ||
          paymentData.walletAddress ||
          paymentData.reference ||
          paymentData.transactionId ||
          paymentData.donationId
        );

      if (isCryptoInit) {
        const reference = paymentData.reference || paymentData.transactionId || null;
        setShowPaymentDetailsModal(false);

        setPendingTxRef(paymentData.txRef || null);
        setPendingTransactionId(paymentData.donationId || paymentData.transactionId || null);
        setPendingReference(reference);
        if (reference) {
          localStorage.setItem("pendingDonateReference", reference);
        } else {
          localStorage.removeItem("pendingDonateReference");
        }
        setCryptoPaymentData(paymentData);
        setShowCryptoPaymentModal(true);
        return;
      }

      throw new Error(response.data?.message || "Failed to initialize donation");
    } catch (err: unknown) {
      console.error("Donation initialization error:", err);
      setErrorMsg(getErrorMessage(err) || "Failed to initialize donation checkout");
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setIsInitializing(false);
    }
  };

  const verifyDonation = async (params?: {
    transactionId?: string | null;
    txRef?: string | null;
    reference?: string | null;
  }) => {
    let verified = false;
    const txId = params?.transactionId || pendingTransactionId || localStorage.getItem("pendingDonateId");
    const txRef = params?.txRef || pendingTxRef || localStorage.getItem("pendingDonateTxRef");
    const reference = params?.reference || pendingReference || localStorage.getItem("pendingDonateReference");
    const query = new URLSearchParams();

    if (txId) query.set("transaction_id", txId);
    if (txRef) query.set("tx_ref", txRef);
    if (reference) query.set("reference", reference);

    if (!query.toString()) return;

    try {
      if (reference) {
        setIsVerifyingCryptoPayment(true);
      } else {
        setIsInitializing(true);
      }

      const response = await apiClient.get(`${ENDPOINTS.DONATE.VERIFY_NGO_DONATION}?${query.toString()}`);

      if (response.data?.success) {
        verified = true;
        setFeedbackData({
          type: 'success',
          title: 'Donation Successful!',
          message: response.data?.message || 'Thank you for your generous contribution.'
        });
        setShowFeedback(true);
        setCustomAmount("");
        setSelectedAmount(null);
        setName("");
        setPayerDetails(DEFAULT_PAYER_DETAILS);
        setCryptoDetails(DEFAULT_CRYPTO_DETAILS);
        localStorage.setItem("walletBalanceRefreshAt", String(Date.now()));
        window.dispatchEvent(new Event("walletBalanceRefresh"));
        try {
          const leaderboardResponse = await apiClient.get(`${ENDPOINTS.DONATE.TOP_DONATORS}?limit=10`);
          const donators = leaderboardResponse.data?.data?.donators;
          setTopDonators(Array.isArray(donators) ? donators : []);
        } catch (leaderboardError) {
          console.error("Failed to refresh top donators", leaderboardError);
        }
      } else {
        throw new Error(response.data?.message || "Payment verification failed");
      }
    } catch (err: unknown) {
      console.error("Verification error:", err);
      const errorMessage =
        getErrorMessage(err) ||
        'Failed to verify donation payment';
      setFeedbackData({
        type: 'error',
        title: 'Verification Failed',
        message: errorMessage
      });
      setShowFeedback(true);
    } finally {
      setIsInitializing(false);
      setIsVerifyingCryptoPayment(false);

      if (verified) {
        setShowCryptoPaymentModal(false);
        setCryptoPaymentData(null);
        setPendingTxRef(null);
        setPendingTransactionId(null);
        setPendingReference(null);
        localStorage.removeItem("pendingDonateTxRef");
        localStorage.removeItem("pendingDonateId");
        localStorage.removeItem("pendingDonateReference");
        localStorage.removeItem("pendingCheckoutFlow");
      }
    }
  };

  return (
    <div className="w-full px-[5rem] max-[600px]:px-[2rem] mt-6">
      <h2 className={`text-[32px] font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
        {t("enduser.supportDonate.title", { defaultValue: "Support Our Cause" })}
      </h2>
      <p className={`mt-4 text-[16px] ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
        {t("enduser.supportDonate.description", {
          defaultValue:
            "Your contribution helps us provide internet access to underserved communities. Every donation, big or small, makes a difference.",
        })}
      </p>

      <div className="mt-10 space-y-8">
        <div>
          <label className={`mb-3 block text-[16px] font-medium ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
            {t("enduser.supportDonate.selectOrganization", { defaultValue: "Select Organization" })}
          </label>
          <Select value={selectedNgo} onValueChange={(value) => setSelectedNgo(value)}>
            <SelectTrigger
              className={`h-[56px] rounded-xl border px-4 text-[16px] ${
                theme === "dark"
                  ? "border-gray-700 bg-[#2b2c2e] text-gray-200"
                  : "border-gray-300 bg-white text-gray-700"
              }`}
            >
              <SelectValue placeholder={t("enduser.supportDonate.selectOrganization", { defaultValue: "Select Organization" })} />
            </SelectTrigger>
            <SelectContent
              side="bottom"
              align="start"
              sideOffset={8}
              avoidCollisions={false}
              className="rounded-xl border border-gray-200 bg-white p-2 shadow-xl dark:border-gray-700 dark:bg-[#1f2022]"
            >
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 dark:border-gray-700 dark:bg-[#2b2c2e]">
                <Search className="h-4 w-4 text-gray-500" />
                <input
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setPage(1);
                    setFetchedNgos([]);
                    setHasMore(true);
                  }}
                  placeholder={t("enduser.donate.searchNgo", { defaultValue: "Search NGO..." })}
                  className="h-8 w-full bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-500 dark:text-gray-100"
                />
              </div>

              {isLoadingNgos && fetchedNgos.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {t("admin.common.loading", { defaultValue: "Loading..." })}
                </div>
              ) : ngoOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {t("enduser.donate.messages.noNgos", { defaultValue: "No NGOs found" })}
                </div>
              ) : (
                ngoOptions.map((ngo) => (
                  <SelectItem key={ngo.id} value={String(ngo.id)}>
                    {ngo.name}
                  </SelectItem>
                ))
              )}

              {hasMore && fetchedNgos.length >= limit && (
                <button
                  type="button"
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={isLoadingMore}
                  className="mt-2 flex w-full items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-[#2b2c2e]"
                >
                  {isLoadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : t("enduser.donate.loadMore", { defaultValue: "Load more" })}
                </button>
              )}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className={`mb-3 block text-[16px] font-medium ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
            {t("enduser.supportDonate.nameOptional", { defaultValue: "Name (Optional)" })}
          </label>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("enduser.supportDonate.yourName", { defaultValue: "Your Name" })}
            className={`w-full rounded-xl h-[56px] border px-5 py-4 text-[16px] outline-none ${
              theme === "dark"
                ? "border-gray-700 bg-[#2b2c2e] text-gray-200 placeholder:text-gray-500"
                : "border-gray-300 bg-white text-gray-700 placeholder:text-gray-400"
            }`}
          />
        </div>

        <div>
          <label className={`mb-3 block text-[16px] font-medium ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
            {t("enduser.donate.customAmount", { defaultValue: "Custom Amount" })}
          </label>
          <input
            type="number"
            min="0"
            value={customAmount}
            onChange={(event) => setCustomAmount(event.target.value)}
            placeholder={t("enduser.donate.enterAmount", { defaultValue: "Enter amount" })}
            className={`w-full rounded-xl h-[56px] border px-5 py-4 text-[16px] outline-none ${
              theme === "dark"
                ? "border-gray-700 bg-[#2b2c2e] text-gray-200 placeholder:text-gray-500"
                : "border-gray-300 bg-white text-gray-700 placeholder:text-gray-400"
            }`}
          />
        </div>

        <div>
          <h3 className={`mb-4 text-[16px] font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
            {t("enduser.donate.selectAmount", { defaultValue: "Select Amount" })}
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {quickAmounts.map((amount) => {
              const isActive = selectedAmount === amount;
              return (
                <button
                  key={amount}
                  type="button"
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomAmount("");
                  }}
                  className={`rounded-xl w-[84px] h-[40px] min-w-[84px] border px-[16px] text-[14px] font-semibold transition ${
                    isActive
                      ? "border-blue-600 bg-blue-600 text-white"
                      : theme === "dark"
                        ? "border-gray-700 bg-[#2b2c2e] text-gray-200 hover:border-gray-500"
                        : "border-gray-200 bg-gray-100 text-gray-800 hover:border-gray-400"
                  }`}
                >
                  ${amount}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className={`mb-4 text-[16px] font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
            {t("enduser.donate.paymentMethod", { defaultValue: "Payment Method" })}
          </h3>
          <div className="flex flex-wrap gap-3">
            {paymentMethods.map((method) => {
              const methodLabel = t(`enduser.donate.methods.${method.toLowerCase()}`, { defaultValue: method });
              const active = paymentMethod === method;
              return (
                <button
                  key={method}
                  type="button"
                  onClick={() => setPaymentMethod(method)}
                  className={`rounded-xl h-[44px] border px-[16px] py-3 text-[14px] font-medium transition ${
                    active
                      ? "border-blue-600 bg-blue-600 text-white"
                      : theme === "dark"
                        ? "border-gray-700 bg-[#2b2c2e] text-gray-200 hover:border-gray-500"
                        : "border-gray-300 bg-white text-gray-800 hover:border-gray-500"
                  }`}
                >
                  {methodLabel}
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={paymentMethod === "Crypto" ? openCryptoPaymentDetails : initializeDonation}
          disabled={isInitializing || donateAmount <= 0}
          className="mt-3 h-[40px] w-full rounded-xl bg-blue-600 px-5 text-[14px] flex items-center justify-center font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition"
        >
          {isInitializing ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-white" />
          ) : (
            <>
              {t("enduser.supportDonate.cta", { defaultValue: "Donate Now" })}
              {donateAmount > 0 ? ` $${donateAmount}` : ""}
            </>
          )}
        </button>

        <div className="pt-4">
          <h3 className={`mb-4 text-[24px] font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
            {t("enduser.supportDonate.topDonors", { defaultValue: "Top Donors" })}
          </h3>
          <div className={`overflow-hidden rounded-xl border ${theme === "dark" ? "border-gray-700" : "border-gray-300"}`}>
            <div className={`grid grid-cols-2 px-4 py-3 text-[16px] font-medium ${theme === "dark" ? "bg-[#2b2c2e] text-gray-200" : "bg-gray-100 text-gray-800"}`}>
              <span>{t("enduser.supportDonate.donor", { defaultValue: "Donor" })}</span>
              <span>{t("enduser.supportDonate.amount", { defaultValue: "Amount" })}</span>
            </div>
            {isLoadingTopDonators ? (
              <div
                className={`flex items-center justify-center gap-2 border-t px-4 py-6 text-sm ${
                  theme === "dark" ? "border-gray-700 text-gray-400" : "border-gray-300 text-gray-500"
                }`}
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t("admin.common.loading", { defaultValue: "Loading..." })}</span>
              </div>
            ) : donorRows.length === 0 ? (
              <div
                className={`border-t px-4 py-6 text-center text-sm ${
                  theme === "dark" ? "border-gray-700 text-gray-400" : "border-gray-300 text-gray-500"
                }`}
              >
                {t("enduser.donate.messages.noLeaderboard", { defaultValue: "No leaderboard data yet" })}
              </div>
            ) : (
              donorRows.map((donor) => (
                <div
                  key={donor.key}
                  className={`grid grid-cols-2 border-t px-4 py-4 text-[16px] ${
                    theme === "dark" ? "border-gray-700 text-gray-300" : "border-gray-300 text-gray-700"
                  }`}
                >
                  <span>{donor.name}</span>
                  <span>{donor.amount}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showSuccess && (
        <div className="fixed top-6 right-6 z-[9999] animate-slide-in">
          <div className="bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
            <CheckCircle className="w-6 h-6" />
            <div>
              <p className="font-semibold">{t("enduser.donate.messages.successTitle", { defaultValue: "Donation Successful!" })}</p>
              <p className="text-sm text-green-100">{t("enduser.donate.messages.successBody", { defaultValue: "Thank you for your generous contribution." })}</p>
            </div>
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="fixed top-6 right-6 z-[9999] animate-slide-in">
          <div className="bg-red-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
            <X className="w-6 h-6" />
            <div>
              <p className="font-semibold">{t("enduser.donate.messages.failedTitle", { defaultValue: "Error" })}</p>
              <p className="text-sm text-red-100">{errorMsg}</p>
            </div>
          </div>
        </div>
      )}

      <FlutterwaveIframeModal
        isOpen={showFlutterwaveIframe}
        paymentLink={flutterwavePaymentLink}
        onClose={() => {
          setShowFlutterwaveIframe(false);
          setFlutterwavePaymentLink(null);
          verifyDonation();
        }}
      />
      <DonationPaymentDetailsModal
        isOpen={showPaymentDetailsModal}
        onClose={() => setShowPaymentDetailsModal(false)}
        onProceed={initializeDonation}
        amount={donateAmount}
        charityLabel={selectedNgoLabel || "NGO"}
        payerDetails={payerDetails}
        onPayerDetailsChange={updatePayerDetails}
        cryptoDetails={cryptoDetails}
        onCryptoDetailsChange={updateCryptoDetails}
        isCryptoTestMode={isCryptoTestMode}
        isProcessing={isInitializing}
      />
      <DonationCryptoPaymentModal
        isOpen={showCryptoPaymentModal}
        data={cryptoPaymentData}
        isVerifying={isVerifyingCryptoPayment}
        onClose={() => {
          setShowCryptoPaymentModal(false);
          setCryptoPaymentData(null);
        }}
        onPaid={() =>
          verifyDonation({
            transactionId:
              pendingTransactionId ||
              cryptoPaymentData?.donationId ||
              cryptoPaymentData?.transactionId ||
              null,
            txRef: pendingTxRef || cryptoPaymentData?.txRef || null,
            reference:
              pendingReference ||
              cryptoPaymentData?.reference ||
              cryptoPaymentData?.transactionId ||
              cryptoPaymentData?.donationId ||
              null,
          })
        }
      />
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={feedbackData.type}
        title={feedbackData.title}
        message={feedbackData.message}
        autoClose={feedbackData.type === 'success'}
        autoCloseDuration={4000}
      />
    </div>
  );
}

interface FlutterwaveIframeModalProps {
  isOpen: boolean;
  paymentLink: string | null;
  onClose: () => void;
}

function FlutterwaveIframeModal({
  isOpen,
  paymentLink,
  onClose,
}: FlutterwaveIframeModalProps) {
  const { t } = useTranslation();
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "[::1]");

  useEffect(() => {
    if (!isOpen || !paymentLink || !isLocalhost) return;
    window.location.assign(paymentLink);
  }, [isOpen, paymentLink, isLocalhost]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-200/90 flex items-center justify-center z-50 p-4">
      <div className="bg-[#f2f2f2] w-[92vw] max-w-[420px] max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-neutral-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-[#f7f7f7]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-neutral-900 flex items-center justify-center">
              <span className="text-white text-sm font-semibold">KX</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">KonectX Donate</p>
              <p className="text-xs text-neutral-500">{t('enduser.donate.secureCheckout', { defaultValue: 'Secure Checkout' })}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-200 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="flex-1 bg-[#f2f2f2]">
          {paymentLink ? (
            isLocalhost ? (
              <div className="h-[70vh] flex items-center justify-center p-6 text-center">
                <div className="space-y-3">
                  <p className="text-sm text-neutral-700">
                    Localhost detected. Opening payment page in your browser.
                  </p>
                  <button
                    onClick={() => window.location.assign(paymentLink)}
                    className="px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm hover:bg-neutral-800 transition-colors"
                  >
                    Open Payment Page
                  </button>
                </div>
              </div>
            ) : (
              <iframe
               
                src={paymentLink}
                className="w-full h-[70vh] border-0 bg-[#f2f2f2]"
                allow="payment"
              />
            )
          ) : (
            <div className="h-[70vh] flex items-center justify-center">
              <p className="text-sm text-neutral-600">{t('enduser.donate.loadingPaymentLink', { defaultValue: 'Loading payment link...' })}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
